/**
 * "What's New" banner — shows the most recent release note the user
 * hasn't dismissed yet. Sits at the top of the authenticated layout
 * (above the navbar's main content area). Dismissal is per-user,
 * persisted server-side, so dismissing on one device clears it on
 * every other.
 *
 * Defensive: render nothing on any API error. Never block the page.
 */
import { useEffect, useState } from "react";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export function WhatsNewBanner() {
  const [item, setItem] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/whats-new")
      .then((r) => setItem((r.data.items || [])[0] || null))
      .catch(() => setItem(null));
  }, []);

  const dismiss = async () => {
    if (!item) return;
    setHidden(true);
    try { await api.post(`/whats-new/dismiss/${item.slug}`); } catch (_) {}
  };

  if (!item || hidden) return null;

  return (
    <div
      className="relative border-b border-ek-purple/15 bg-gradient-to-r from-ek-purple/5 to-transparent"
      data-testid="whats-new-banner"
    >
      <div className="mx-auto flex max-w-6xl items-start gap-3 px-6 py-2.5">
        <Sparkles size={14} className="mt-0.5 shrink-0 text-ek-purple" />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold text-ek-ink">{item.title}</span>
          <span className="ml-2 text-ek-ink/65">{item.body}</span>
          {item.link && (
            <Link
              to={item.link}
              className="ml-2 inline-flex items-center gap-1 font-semibold text-ek-purple hover:underline"
              data-testid="whats-new-cta"
            >
              See it <ArrowRight size={11} />
            </Link>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 rounded-md p-1 text-ek-ink/45 transition hover:bg-ek-purple/10 hover:text-ek-purple"
          aria-label="Dismiss this update"
          data-testid="whats-new-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
